// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullAdminRegatta,
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  RegattaGroupRatingType,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdImage,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import {
  cleanFormValues,
  getImageUrl,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  ratingTypeLabel,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../../helpers';
import config from '../../../../config';

// Components
import EmptyTable from '../../../common/EmptyTable';
import RegattaGroupFields from '../../../fields/RegattaGroupFields';
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Types
type $Props = $Translate & {
  id: string;
  item: $FullAdminRegatta;
};

type $State = {
  data: Array<$Model<'RegattaGroup'>>;
  loading: boolean;
  resourceData: Array<$Model<'Group'>>;
};

class RegattaGroupsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      resourceData: [],
    };
  }

  componentDidMount(): void {
    this.getData();
    this.getResourceData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getData();
      this.getResourceData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.RegattaId && data.modelName === 'RegattaGroup') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyRegattaGroup({
          RegattaId,
        });

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  async getResourceData(): Promise<void> {
    await defaultGetData({
      action: async () => {
        const resourceData = await Services.Api.getResourceGroup();

        await this.setStateAsync({
          resourceData,
        });
      },
    });
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;
    const {
      resourceData,
    } = this.state;

    const RegattaId = item.id;

    const newItem: $ModelUpsertParams<'RegattaGroup'> = {
      AltName: null,
      CoachEnabled: false,
      CrewQuantityLimit: null,
      EntriesQuantityLimit: null,
      GroupId: '',
      PreliminaryCrewQuantityEnabled: false,
      RatingType: RegattaGroupRatingType.monotype,
      RegattaId,
    };

    return (
      <SectionHeader
        bottomBorder
        description={'A list of regatta entry groups listed on the regatta registration form'}
        iconClass={'icon-layers'}
        rightButtons={(
          <FormDialog
            fields={(
              <RegattaGroupFields
                item={newItem}
                resourceData={resourceData}
              />
            )}
            id={'create-regatta-group-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'RegattaGroup'>>({
                  ...newItem,
                  ...values,
                });

                await Services.Api.upsertRegattaGroup(params);
              },
            })}
            subTitle={t('New')}
            title={t('Regatta group')}
          >
            <Button
              color={'info'}
              id={'create-regatta-group-button'}
              size={'sm'}
              title={t('Add regatta group')}
            >
              {t('Add new')}
            </Button>
          </FormDialog>
        )}
        title={t('Regatta groups')}
      />
    );
  }

  renderButtons(dataItem: $Model<'RegattaGroup'>) {
    const {
      t,
    } = this.props;
    const {
      resourceData,
    } = this.state;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <RegattaGroupFields
              item={dataItem}
              resourceData={resourceData}
            />
          )}
          id={`update-regatta-group-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'RegattaGroup'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertRegattaGroup(params);
            },
          })}
          subTitle={dataItem.AltName || _.get(
            _.find(
              resourceData,
              {
                id: dataItem.GroupId,
              },
            ),
            'Name',
            '',
          )}
          title={t('Regatta registration group')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-regatta-group-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-group-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteRegattaGroup(dataItem),
          })}
          subTitle={dataItem.AltName || _.get(
            _.find(
              resourceData,
              {
                id: dataItem.GroupId,
              },
            ),
            'Name',
            '',
          )}
          title={t('Regatta registration group')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-group-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderImageTd(dataItem: $Model<'RegattaGroup'>) {
    const {
      resourceData,
    } = this.state;

    const value: null | string = _.get(
      _.find(
        resourceData,
        {
          id: dataItem.GroupId,
        },
      ),
      'Image',
      null,
    );

    return (
      <TdImage
        largeImageUrl={getImageUrl({
          cdnUrl: config.CDN_URL,
          image: value,
          size: 'max',
        })}
        noImageUrl={getImageUrl({
          cdnUrl: config.CDN_URL,
          image: null,
          size: 'min',
        })}
        smallImageUrl={getImageUrl({
          cdnUrl: config.CDN_URL,
          image: value,
          size: 'min',
        })}
        value={value}
      />
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
      resourceData,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th />
            <th className={'py-1'}>{t('Group')}</th>
            <th className={'py-1'}>{t('Rating type')}</th>
            <th className={'py-1 text-center'}>{t('Coach')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'RegattaGroup'>) => (
            <tr key={dataItem.id}>
              {this.renderImageTd(dataItem)}
              <td className={'py-1'}>
                {dataItem.AltName || _.get(
                  _.find(
                    resourceData,
                    {
                      id: dataItem.GroupId,
                    },
                  ),
                  'Name',
                  '',
                )}
              </td>
              <td className={'py-1'}>{ratingTypeLabel[dataItem.RatingType]}</td>
              <td className={'text-center'}>
                {dataItem.CoachEnabled === true && (
                  <em className={'icon-check'} />
                )}
              </td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default RegattaGroupsSectionView;
